import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Typography,
  Autocomplete,
  TextField,
  MenuItem
} from '@mui/material';
import { Folder, InsertDriveFile as FileIcon, Assignment as SurveyIcon } from '@mui/icons-material';
import { useOktaAuth } from '@okta/okta-react';
import { useTenant } from '../../contexts/TenantContext';
import { useProjectProgress } from '../../contexts/ProjectProgressContext';
import useApi from '../../hooks/useApi';
import { createRuntimeApiClient } from '../../services/runtimeApiInterceptor';
import Breadcrumb from '../../components/common/Breadcrumb';
import { useErrorToast } from '../../components/common';
import { getApiErrorMessage } from '../../utils/errorMessages';
import './LandingPage.css';

// Resolve a survey's lifecycle status key from the summary shape. Mirrors the
// logic in SurveyManagePage's getSurveyStatus so "closed" means the same thing
// in both places.
function surveyStatusKey(s) {
  const state = s.live_window_state;
  if (s.is_recurrence_parent) {
    if (state === 'open' || s.is_live_window_open) return 'live';
    if (state === 'closed') return 'closed';
    return 'scheduled';
  }
  if (s.status === 'draft') return 'draft';
  if (state === 'open' || s.is_live_window_open) return 'live';
  if (state === 'not_open') return 'scheduled';
  if (state === 'closed' || s.status === 'closed') return 'closed';
  if (s.status === 'published') return 'live';
  return 'closed';
}

function isSurveyClosed(s) {
  return surveyStatusKey(s) === 'closed';
}

function hasResponses(s) {
  return (s.response_count ?? 0) > 0;
}

// Relative "date created" filter options for the survey picker.
const SURVEY_DATE_FILTERS = [
  { value: 'all', label: 'Any time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '1m', label: 'Last month' },
  { value: '3m', label: 'Last 3 months' },
  { value: '6m', label: 'Last 6 months' },
  { value: '1y', label: 'Last year' },
];

// Earliest creation date (inclusive) a survey may have to pass the filter;
// null means no lower bound.
function surveyDateCutoff(value) {
  if (value === 'all') return null;
  const d = new Date();
  switch (value) {
    case '7d':
      d.setDate(d.getDate() - 7);
      break;
    case '1m':
      d.setMonth(d.getMonth() - 1);
      break;
    case '3m':
      d.setMonth(d.getMonth() - 3);
      break;
    case '6m':
      d.setMonth(d.getMonth() - 6);
      break;
    case '1y':
      d.setFullYear(d.getFullYear() - 1);
      break;
    default:
      return null;
  }
  return d;
}

const LandingPage = () => {
  const navigate = useNavigate();
  const { authState } = useOktaAuth();
  const { currentTenant, hasPermission } = useTenant();
  const { initializeProject } = useProjectProgress();
  const api = useApi();
  const { showErrorToast } = useErrorToast();
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [dataSource, setDataSource] = useState('external');
  const [projectName, setProjectName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [nameExists, setNameExists] = useState(false);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const checkNameTimerRef = useRef(null);

  const [surveys, setSurveys] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [isLoadingSurveys, setIsLoadingSurveys] = useState(false);
  const [surveyDateFilter, setSurveyDateFilter] = useState('all');

  // Only closed surveys can be used as a data source. Surveys with no collected
  // responses are still shown (see renderOption) but disabled.
  const closedSurveys = useMemo(() => surveys.filter(isSurveyClosed), [surveys]);

  useEffect(() => {
    if (dataSource === 'inApp' && currentTenant && authState?.idToken?.idToken) {
      const fetchSurveys = async () => {
        setIsLoadingSurveys(true);
        try {
          const runtimeApi = createRuntimeApiClient(authState, currentTenant.tenantId);
          const response = await runtimeApi.get('/admin/surveys');
          setSurveys(response.data);
        } catch (error) {
          console.error(error);
          showErrorToast('Error loading surveys');
        } finally {
          setIsLoadingSurveys(false);
        }
      };
      fetchSurveys();
    }
  }, [dataSource, currentTenant, authState, showErrorToast]);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const checkProjectName = useCallback((name) => {
    if (checkNameTimerRef.current) clearTimeout(checkNameTimerRef.current);

    const trimmed = name.trim();
    if (!trimmed) {
      setNameExists(false);
      setIsCheckingName(false);
      return;
    }

    setIsCheckingName(true);
    checkNameTimerRef.current = setTimeout(async () => {
      try {
        const response = await api.get(`/projects/check-name?name=${encodeURIComponent(trimmed)}`);
        setNameExists(response.data?.data?.exists === true);
      } catch (err) {
        console.error('Error checking project name:', err);
        setNameExists(false);
      } finally {
        setIsCheckingName(false);
      }
    }, 400);
  }, [api]);

  useEffect(() => {
    return () => {
      if (checkNameTimerRef.current) clearTimeout(checkNameTimerRef.current);
    };
  }, []);

  const handleProjectNameChange = useCallback((name) => {
    setProjectName(name);
    checkProjectName(name);
  }, [checkProjectName]);

  const handleFileChange = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('File size should not exceed 5MB', 'error');
      return;
    }

    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      showSnackbar('Invalid file type. Please upload a CSV or Excel file.', 'error');
      return;
    }

    setSelectedFile(file);
    setError(null);
  }, [showSnackbar]);

  const uploadToServer = useCallback(async (projectName, file) => {
    try {
      if (!currentTenant) {
        throw new Error('No tenant context available');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectName', projectName?.trim());
      formData.append('tenantId', currentTenant.tenantId);
      
      const userName = authState?.idToken?.claims?.name || 'Guest User';
      const userEmail = authState?.idToken?.claims?.email || authState?.idToken?.claims?.sub;
      formData.append('createdBy', userName);
      formData.append('userEmail', userEmail);

      console.log('Uploading file to tenant:', currentTenant.tenantName);
      console.log('FormData contents:', {
        projectName: projectName?.trim(),
        tenantId: currentTenant.tenantId,
        fileName: file.name,
        fileSize: file.size,
        createdBy: userName,
        userEmail: userEmail,
      });

      const response = await api.post('/jobs/upload', formData);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Upload failed');
      }

      const result = response.data;
      console.log('Upload response:', result);
      return result;
    } catch (error) {
      console.error('Error uploading file:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });
      
      const errorMessage = getApiErrorMessage(error, 'Error uploading file. Please try again.');
      
      const enhancedError = new Error(errorMessage);
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }, [authState, currentTenant, api]);

  const processAndConvert = useCallback(async () => {
    if (!selectedFile) return;
    
    if (!hasPermission('upload_data')) {
      showSnackbar('You don\'t have permission to upload files', 'error');
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      setShowFullScreenLoader(true);
      showSnackbar('Processing file... This may take a few minutes for large files.', 'info');
      
      const response = await uploadToServer(projectName, selectedFile);
      
      if (response.success && response.data) {
        const uploadData = response.data;
        const projectId = uploadData.projectId; // Project already created during upload
        
        // Store file metadata in localStorage for navigation
        localStorage.setItem('currentUpload', JSON.stringify({
          projectId: projectId,
          fileName: uploadData.fileName,
          tableName: uploadData.tableName,
          projectName: uploadData.projectName || projectName,
        }));
        
        // Initialize project progress tracking with file metadata
        await initializeProject({
          projectId: projectId, // Project already exists
          fileName: uploadData.fileName,
          displayName: uploadData.displayName || projectName,
          projectName: uploadData.projectName || projectName,
          tableName: uploadData.tableName,
        });
        
        showSnackbar('File processed successfully!', 'success');
        
        setTimeout(() => {
          setShowFullScreenLoader(false);
          // Use projectId for navigation (Project already exists)
          navigate(`/preview/${projectId}`);
        }, 1000);
      } else {
        throw new Error(response.error || 'Processing failed');
      }
    } catch (error) {
      console.error('Error processing file:', error);
      const errorMessage = getApiErrorMessage(error, 'Error processing file. Please try again.');
      setError(null);
      showErrorToast(errorMessage);
      setShowFullScreenLoader(false);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile, projectName, uploadToServer, showSnackbar, navigate, hasPermission]);

  const importFromSurvey = useCallback(async () => {
    if (!selectedSurveyId || !projectName.trim()) return;
    
    setIsLoading(true);
    setError(null);

    try {
      setShowFullScreenLoader(true);
      showSnackbar('Importing survey responses... This may take a moment.', 'info');
      
      const response = await api.post('/jobs/import-survey', {
        surveyId: selectedSurveyId,
        projectName: projectName.trim(),
        tenantId: currentTenant.tenantId
      });
      
      if (response.status === 201 || response.status === 200) {
        const uploadData = response.data.data || response.data;
        const projectId = uploadData.projectId;
        
        localStorage.setItem('currentUpload', JSON.stringify({
          projectId: projectId,
          fileName: uploadData.fileName,
          tableName: uploadData.tableName,
          projectName: uploadData.projectName || projectName,
        }));
        
        await initializeProject({
          projectId: projectId,
          fileName: uploadData.fileName,
          displayName: uploadData.displayName || projectName,
          projectName: uploadData.projectName || projectName,
          tableName: uploadData.tableName,
        });
        
        showSnackbar('Survey imported successfully!', 'success');
        
        setTimeout(() => {
          setShowFullScreenLoader(false);
          navigate(`/preview/${projectId}`);
        }, 1000);
      } else {
        throw new Error(response.data?.error || 'Import failed');
      }
    } catch (error) {
      console.error('Error importing survey:', error);
      const errorMessage = getApiErrorMessage(error, 'Error importing survey. Please try again.');
      setError(null);
      showErrorToast(errorMessage);
      setShowFullScreenLoader(false);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSurveyId, projectName, currentTenant, api, showSnackbar, navigate, initializeProject, showErrorToast]);

  const handleFileUpload = useCallback(() => {
    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }
    if (dataSource === 'inApp') {
      importFromSurvey();
    } else {
      processAndConvert();
    }
  }, [dataSource, projectName, importFromSurvey, processAndConvert]);

  const handleSnackbarClose = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const isExternalSource = dataSource === 'external';

  return (
    <>
      <Breadcrumb currentStep="upload" />
      <div className="landing-container">
        <div className="landing-content">
          <div className="landing-grid">
            <div className="landing-left-section">
              <div className="landing-hero-placeholder">
              </div>
            </div>

            <div className="landing-right-section">
              {currentTenant && (
                <div className="landing-tenant-badge">
                  Tenant: {currentTenant.tenantName}
                </div>
              )}
              
              <h1 className="landing-title">
                "Flexible Ingestion for Evolving Data"
              </h1>
              <p className="landing-subtitle">
                Any Structure, Any Number of Columns, Any Sequence
              </p>

              <div className="landing-import-section">
                <h2 className="landing-import-title">
                  Import your data for analysis
                </h2>
                <p className="landing-import-subtitle">
                  Desired file format: csv, xlsx, xls
                </p>

                <div className="landing-source-selector" role="tablist" aria-label="Data source selector">
                  <button
                    type="button"
                    className={`landing-source-option${isExternalSource ? ' active' : ''}`}
                    onClick={() => setDataSource('external')}
                    aria-pressed={isExternalSource}
                  >
                    External file
                  </button>
                  <button
                    type="button"
                    className={`landing-source-option${!isExternalSource ? ' active' : ''}`}
                    onClick={() => setDataSource('inApp')}
                    aria-pressed={!isExternalSource}
                  >
                    From in-app surveys
                  </button>
                </div>
                
                <div className="landing-steps">
                  {/* Step 1: Select Source */}
                  <div className="landing-step">
                    <div className="landing-step-number">1</div>
                    {isExternalSource ? (
                      <div className="landing-step-action">
                        <input
                          id="file-upload"
                          type="file"
                          accept=".csv, .xlsx, .xls"
                          onChange={handleFileChange}
                          style={{ display: 'none' }}
                          disabled={isLoading}
                        />
                        <button
                          className="landing-browse-button"
                          onClick={() => document.getElementById('file-upload').click()}
                          disabled={isLoading}
                        >
                          <Folder fontSize="small" />
                          Browse
                        </button>
                      </div>
                    ) : (
                      <div className="landing-step-action" style={{ width: '100%', maxWidth: '300px' }}>
                        {isLoadingSurveys ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666' }}>
                            <CircularProgress size={20} color="inherit" />
                            <span>Loading surveys...</span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Created"
                            value={surveyDateFilter}
                            onChange={(e) => setSurveyDateFilter(e.target.value)}
                            disabled={isLoading || closedSurveys.length === 0}
                            sx={{ backgroundColor: '#fff', borderRadius: '8px' }}
                          >
                            {SURVEY_DATE_FILTERS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>
                          <Autocomplete
                            fullWidth
                            size="small"
                            options={closedSurveys}
                            value={closedSurveys.find((s) => s.id === selectedSurveyId) || null}
                            disabled={isLoading || closedSurveys.length === 0}
                            getOptionLabel={(survey) => survey?.title || ''}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionDisabled={(survey) => !hasResponses(survey)}
                            filterOptions={(options, { inputValue }) => {
                              const term = inputValue.trim().toLowerCase();
                              const cutoff = surveyDateCutoff(surveyDateFilter);
                              return options.filter((survey) => {
                                if (cutoff) {
                                  const createdRaw = survey.created_at || survey.updated_at;
                                  const created = createdRaw ? new Date(createdRaw) : null;
                                  if (!created || Number.isNaN(created.getTime()) || created < cutoff) {
                                    return false;
                                  }
                                }
                                if (!term) return true;
                                return (
                                  (survey.title || '').toLowerCase().includes(term) ||
                                  String(survey.id).includes(term)
                                );
                              });
                            }}
                            onChange={(e, survey) => {
                              setSelectedSurveyId(survey ? survey.id : '');
                              if (survey && !projectName) {
                                handleProjectNameChange(survey.title);
                              }
                            }}
                            noOptionsText="No closed surveys found"
                            renderOption={(props, survey) => {
                              const createdRaw = survey.created_at || survey.updated_at;
                              const created = createdRaw ? new Date(createdRaw) : null;
                              const createdLabel =
                                created && !Number.isNaN(created.getTime())
                                  ? created.toLocaleDateString()
                                  : null;
                              const noResponses = !hasResponses(survey);
                              return (
                                <li
                                  {...props}
                                  key={survey.id}
                                  style={{ opacity: noResponses ? 0.55 : 1 }}
                                >
                                  <span
                                    style={{
                                      marginRight: '8px',
                                      color: '#888',
                                      fontVariantNumeric: 'tabular-nums',
                                      fontWeight: 600,
                                    }}
                                  >
                                    #{survey.id}
                                  </span>
                                  <span style={{ flex: 1 }}>{survey.title}</span>
                                  {noResponses ? (
                                    <span
                                      style={{
                                        marginLeft: '8px',
                                        color: '#b26a00',
                                        fontSize: '0.78em',
                                        fontStyle: 'italic',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      No responses collected
                                    </span>
                                  ) : (
                                    createdLabel && (
                                      <span style={{ marginLeft: '8px', color: '#999', fontSize: '0.8em' }}>
                                        {createdLabel}
                                      </span>
                                    )
                                  )}
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Survey"
                                placeholder="Search by ID or name…"
                              />
                            )}
                            sx={{
                              backgroundColor: '#fff',
                              borderRadius: '8px',
                            }}
                          />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedFile && isExternalSource && (
                    <div className="landing-file-info">
                      <FileIcon fontSize="small" className="landing-file-info-icon" />
                      <span className="landing-file-info-name">{selectedFile.name}</span>
                      <span className="landing-file-info-size">
                        ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
                      </span>
                    </div>
                  )}
                  {selectedSurveyId && !isExternalSource && (
                    <div className="landing-file-info">
                      <SurveyIcon fontSize="small" className="landing-file-info-icon" />
                      <span className="landing-file-info-name">
                        {surveys.find(s => s.id === selectedSurveyId)?.title}
                      </span>
                    </div>
                  )}

                  {/* Step 2: Enter project name */}
                  <div className="landing-step">
                    <div className="landing-step-number">2</div>
                    <div className="landing-step-input-wrapper">
                      <input
                        type="text"
                        className={`landing-project-name-input${nameExists ? ' input-error' : ''}`}
                        placeholder="Enter dashboard name"
                        value={projectName}
                        onChange={(e) => handleProjectNameChange(e.target.value)}
                        disabled={isLoading}
                      />
                      {nameExists && (
                        <div className="landing-step-helper error">
                          A project with this name already exists in this tenant
                        </div>
                      )}
                      {isCheckingName && (
                        <div className="landing-step-helper checking">
                          Checking name availability...
                        </div>
                      )}
                    </div>
                  </div>

                </div>

                {error && (
                  <div className="landing-step-error">
                    {error}
                  </div>
                )}

                <div className="landing-primary-action">
                  <button
                    className="landing-upload-button"
                    onClick={handleFileUpload}
                    disabled={
                      !projectName.trim() ||
                      nameExists ||
                      isCheckingName ||
                      isLoading ||
                      (isExternalSource ? !selectedFile : !selectedSurveyId)
                    }
                  >
                    {isLoading ? (
                      <>
                        <CircularProgress size={18} color="inherit" />
                        Processing...
                      </>
                    ) : (
                      'Continue'
                    )}
                  </button>
                </div>
                
                <div className="landing-file-notice">
                  Upload file size not exceeding 5MB
                </div>
              </div>
            </div>
          </div>
        </div>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Backdrop
          sx={{ 
            color: '#fff', 
            zIndex: (theme) => theme.zIndex.drawer + 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
          open={showFullScreenLoader}
        >
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ marginTop: 2 }}>
            Processing your file...
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Please wait while we analyze your data
          </Typography>
        </Backdrop>
      </div>
    </>
  );
};

export default LandingPage;
